import cookie from 'react-cookies';
import AuthApiConsumer from '../../core/auth/api';
import {
  buildStoredUserMeta,
  extractRefreshTokenId,
  normalizeLoginPayload,
} from '../../shared/auth/normalizeLoginPayload';

const REFRESH_COOKIE = 'beon_refresh_token';

const AuthApiFactory = () => {
  const isAuthenticated = () => {
    try {
      const token = cookie.load('beon_auth_token');
      return token ? true : false;
    } catch (error) {
      return false;
    }
  };

  const isAuthorized = scope => {
    try {
      const { acl } = getAuthenticated();

      if (!acl) {
        return false;
      }

      const hasGlobalAcl = Object.keys(acl).indexOf(`bn:global`);
      const hasOrgAcl = Object.keys(acl).indexOf(`org:${scope}`);

      return hasGlobalAcl > -1 || hasOrgAcl > -1;
    } catch (error) {
      console.log(error);
      return false;
    }
  };

  const setAuthenticated = ({ token, user, acl, meta }) =>
    new Promise(async resolve => {
      try {
        cookie.save('beon_auth_token', token, { path: '/' });
        const refreshId = extractRefreshTokenId(meta);
        if (refreshId) {
          cookie.save(REFRESH_COOKIE, refreshId, { path: '/' });
        }

        // user is local stored
        localStorage.setItem('current_user', user);
        localStorage.setItem('current_user_meta', JSON.stringify(meta));
        localStorage.setItem('current_user_acl', JSON.stringify(acl));

        resolve(true);
      } catch (error) {
        resolve(error);
      }
    });

  const destroyAuthenticated = () =>
    new Promise(async resolve => {
      cookie.remove('beon_auth_token', { path: '/' });
      cookie.remove(REFRESH_COOKIE, { path: '/' });

      // user is local stored
      localStorage.clear();
      resolve();
    });

  const getAuthenticated = () => {
    try {
      const user = localStorage.getItem('current_user');
      const acl = JSON.parse(localStorage.getItem('current_user_acl'));
      const meta = JSON.parse(localStorage.getItem('current_user_meta'));

      const storageScope = localStorage.getItem('current_scope');
      const scope = storageScope ? JSON.parse(storageScope) : undefined;

      return { user, acl, scope, meta };
    } catch (error) {
      console.log(error);
      return { user: undefined, acl: undefined, scope: undefined };
    }
  };

  const authenticate = ({ username, password }) =>
    new Promise(async resolve => {
      AuthApiConsumer.authenticate({ username, password }).then(result => {
        const status = {
          success: undefined,
          user: username,
          meta: undefined,
          acl: undefined,
          token: undefined,
        };

        if (result instanceof Error) {
          status.success = false;
        } else {
          const normalized = normalizeLoginPayload(result);
          status.success = Boolean(normalized);
          if (normalized) {
            status.acl = normalized.acl;
            status.token = normalized.accessToken;
            status.meta = buildStoredUserMeta(normalized);
          }
        }

        resolve(status);
      });
    });

  const refreshAuth = () =>
    new Promise(async resolve => {
      resolve(isAuthenticated());
    });

  const login = async (username, password) =>
    new Promise(async resolve => {
      if (isAuthenticated()) await destroyAuthenticated();

      const authResponse = await authenticate({ username, password });

      if (authResponse.success) {
        await setAuthenticated(authResponse);
        resolve(true);
      } else {
        resolve(new Error(authResponse.message));
      }
    });

  const logout = () =>
    new Promise(async resolve => {
      await destroyAuthenticated();
      resolve();
    });

  const setScope = scope => {
    localStorage.setItem('current_scope', JSON.stringify(scope));

    return scope;
  };

  const getScope = () => {
    const scope = localStorage.getItem('current_scope');

    if (scope) return JSON.parse(scope);

    // returns empty object to allow destructuring
    return {};
  };

  return Object.freeze({
    isAuthenticated,
    isAuthorized,
    getAuthenticated,
    login,
    logout,
    refreshAuth,
    setScope,
    getScope,
  });
};

export default AuthApiFactory;
